import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "../../style/olePatrolsPage.scss";
import { useDispatch, useSelector } from "react-redux";
import {
    getShortcuts,
    selectShortcutSession,
    requestPlaylistPosition,
    togglePictureInPicture,
    videoOrImagePlayerTogglePlay,
    videoPlayerSeekTo,
    goToBounds,
    changeToolMode,
    getPagedRouteHistory,
    getSessionData,
    toggleSnapshot,
    getNearbyAssets,
    setAssetSearchType,
    receiveNearbyAssets,
    selectMapPoint,
    changeDashboard,
} from "redux/actions";

import { Modal, Button, Tooltip, Select, Popover, notification } from "antd";
import _ from "lodash";
import { getBearingOptions, getSourceList } from "./olePatrolsUtils";
import { sourceOffsetSelector } from "redux/selectors";
import { absoluteTimeLookup, videoKeyToTimestamp, videoTimestampExtractor } from "components/util/PlaylistUtils";
import { coordinateToPlaylistDistance, getRoutePosAtTimestamp, playlistDistanceToTimestamp } from "components/util/GeometryUtilsV2";
import OlePatrolsStillsPreview from "./OlePatrolsStillsPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faImage, faSearchLocation, faSearchPlus, faVideoSlash } from "@fortawesome/pro-regular-svg-icons";
import { faMapMarkerAlt, faSlidersH } from "@fortawesome/pro-solid-svg-icons";
import { ReactComponent as OhlSvg } from "../../icons/ohl.svg";
import OBCSpinner from "components/util/OBC";
import {
    calculateBearingText,
    calculateElrGroupsForVideo,
    findIndexAtDistanceV2,
    nearestPointOnGeometry,
    parseRoutePositionData,
} from "components/util/Geometry";
import UnitSelectionModal from "components/UnitSelectionModal";
import MapComponent from "components/map/MapComponent";
import ResizableDivider from "components/ResizableDivider";
import RouteCoordinateSystems from "components/util/RouteCoordinateSystems";
import VideoOrImageComponent from "components/display/VideoOrImageComponent";
import StandAloneVideoOrImageComponent from "components/display/StandAloneVideoOrImageComponent";
import moment from "moment";
import ImageAdjustmentsComponent from "components/display/image/ImageAdjustmentsComponent";
import CustomSlider from "./CustomSlider";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const ALLOWED_ASSETS_TYPES = ["OHL Structure"];

const shortcutsSelector = (state) => state.shortcuts.shortcuts;
const isStillsModeSelector = (state) => state.playlist.position.isStills;
const currentPositionCoordsSelector = (state) => state.playlist.position.coords;
const fullPlaylistSelector = (state) => state.playlist;
const playerStateSelector = (state) => state.playlist.position.playerState;
const isEnhancedSelector = (state) => state.playlist.position.isEnhanced;
const isVideoSelector = (state) => state.playlist.position.isVideo;
const sourceIndexSelector = (state) => state.playlist.position.sourceIndex;
const currentIndexSelector = (state) => state.playlist.position.currentIndex;
const currentTimeOffsetSelector = (state) => state.playlist.position.currentTimeOffset;
const playlistDistanceSelector = (state) => state.playlist.position.playlistDistance;
const playlistDataSelector = (state) => state.playlist.data;
const routeLocationsV2Selector = (state) => state.playlist.data.route_locations_v2;
const sessionsSelector = (state) => state.sessions;
const routePositionSelector = (state) => state.playlist.position.routePosition;
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const dashboardsSelector = (state) => state.dashboards;
const userDetailsSelector = (state) => state.userDetails;
const viewsSelector = (state) => state.views;
const showPictureInPictureSelector = (state) => state.showPictureInPicture;
const playerOrImageLoadingSelector = (state) => state.playlist.position.isLoading;
const currentMapBoundsSelector = (state) => state.sessionFilters.mapBounds.current;
const toolModeSelector = (state) => state.markup.tool_mode;
const fetchingHistorySelector = (state) => state.playlist.consolidatedHistory.fetching;
const consolidatedHistorySelector = (state) => state.playlist.consolidatedHistory.history;
const mainPlayerLoadedSelector = (state) => state.playlist.data.loaded;
const initialBoundsSelector = (state) => state.playlist.position.initialBounds;
const selectedMarkerSelector = (state) => state.markers.selectedMarker;
const geometrySelector = (state) => state.mapGeometry;
const assetsFoundSelector = (state) => state.assets.assetsFound;
// const offsetSelector = (state) => state.playlist.data.offsets;

const ButtonGroup = Button.Group;
const CONTROL_BUTTONS_SIZE = "default";

const DEFAULT_EXPLORING_FILTERS = {
    day_time: "Day",
    extra_type: -1,
    days_in_past: 14,
    video_facing: -1,
};

const calculateDirectionText = (tags) => {
    const forwardOrBackward = _.includes(tags, "Forward") ? "Forward" : _.includes(tags, "Backward") ? "Backward" : "Unknown";
    return forwardOrBackward;
};

const SessionListItem = ({ active, session, setExploringLocationSession }) => {
    const dispatch = useDispatch();
    const [tooltipText, setTooltipText] = useState("Click to Copy");

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(session.session_id);

        setTooltipText("Copied!");
        setTimeout(() => {
            setTooltipText("Click to Copy");
        }, 1200);
    };

    return (
        <div
            key={session.session_id}
            className={`OlePatrolsExploringSessionsListItem ${active ? "Selected" : ""}`}
            onClick={() => {
                if (active) return;

                setExploringLocationSession(null); // clear current session while loading new one
                dispatch(getSessionData(session.session_id))
                    .then((sessionData) => {
                        setExploringLocationSession(sessionData);
                    })
                    .catch((e) => {
                        setExploringLocationSession(null);
                    });
            }}>
            <Tooltip title={tooltipText}>
                <div
                    className="OlePatrolsExploringSessionsListItemSessionID"
                    onClick={handleCopy}
                    style={{ cursor: "pointer" }}>
                    #{session.session_id}
                </div>
            </Tooltip>
            <div className="OlePatrolsExploringSessionsListItemMain">
                <div className="OlePatrolsExploringSessionsListItemTitle">{session.name}</div>
                <div className="OlePatrolsExploringSessionsListItemInfoPane">
                    <div className="OlePatrolsExploringSessionsListItemDate">{moment(session.timestamp).format("DD MMM YYYY HH:mm")}</div>
                </div>
            </div>
            <div className="OlePatrolsExploringSessionsListItemEnd">
                <div
                    className="OlePatrolsExploringSessionsListItemCol"
                    style={{ minWidth: "70px" }}>
                    <div>Direction:</div>
                    <div className="OlePatrolsExploringSessionsListItemTag">{calculateDirectionText(session.session_tags)}</div>
                </div>

                <div
                    className="OlePatrolsExploringSessionsListItemCol"
                    style={{ minWidth: "45px" }}>
                    <div>Track:</div>
                    <div className="OlePatrolsExploringSessionsListItemTag">{session.sub_position}</div>
                </div>

                <div
                    className="OlePatrolsExploringSessionsListItemCol"
                    style={{ minWidth: "48px" }}>
                    <div>Bearing:</div>
                    <div className="OlePatrolsExploringSessionsListItemTag">{calculateBearingText(session.bearing)}</div>
                </div>
            </div>
        </div>
    );
};

const OlePatrolPage = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const titleUpdatedRef = useRef(false);

    const dashboards = useSelector(dashboardsSelector);
    const userDetails = useSelector(userDetailsSelector);
    const views = useSelector(viewsSelector);
    const shortcuts = useSelector(shortcutsSelector);
    const isStillsMode = useSelector(isStillsModeSelector);
    const fullPlaylist = useSelector(fullPlaylistSelector);
    const playerState = useSelector(playerStateSelector);
    const playlistData = useSelector(playlistDataSelector);
    const sessions = useSelector(sessionsSelector);
    const isEnhanced = useSelector(isEnhancedSelector);
    const isVideo = useSelector(isVideoSelector);
    const currentSourceIndex = useSelector(sourceIndexSelector);
    const currentIndex = useSelector(currentIndexSelector);
    const currentTimeOffset = useSelector(currentTimeOffsetSelector);
    const playlistDistance = useSelector(playlistDistanceSelector);
    const routeLocationsV2 = useSelector(routeLocationsV2Selector);
    const routePosition = useSelector(routePositionSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const showPictureInPicture = useSelector(showPictureInPictureSelector);
    const playerOrImageLoading = useSelector(playerOrImageLoadingSelector);
    const currentMapBounds = useSelector(currentMapBoundsSelector);
    const currentPositionCoords = useSelector(currentPositionCoordsSelector);
    const toolMode = useSelector(toolModeSelector);
    const fetchingHistory = useSelector(fetchingHistorySelector);
    const consolidatedHistory = useSelector(consolidatedHistorySelector);
    const mainPlayerLoaded = useSelector(mainPlayerLoadedSelector);
    const initialBounds = useSelector(initialBoundsSelector);
    const selectedMarker = useSelector(selectedMarkerSelector);
    const geometry = useSelector(geometrySelector);

    // const cameraOffsets = useSelector(offsetSelector); // this will probably need to be used to get more precise position
    const assetsFound = useSelector(assetsFoundSelector);

    const dashboardID = userDetails.dashboardAccessID;
    const currentDashboard = _.find(dashboards, (dash) => dash.access_id === dashboardID);
    const userViewOffsets = _.get(views, [userDetails.userConfig.view_id, "datum_offsets"], []);
    const workspaceViewOffsets = _.get(views, [_.get(currentDashboard, ["config", "view_id"], -1), "datum_offsets"], []);

    const datumOffsets = workspaceViewOffsets.length ? workspaceViewOffsets : userViewOffsets;

    const [playButtonPaused, setPlayButtonPaused] = useState(true);
    const [unitSelectionModalVisible, setUnitSelectionModalVisible] = useState(false);
    const [resizingMap, setResizingMap] = useState(false);
    const [sourcesMap, setSourcesMap] = useState({});
    const [selectedSection, setSelectedSection] = useState(null);
    const [updatingPosition, setUpdatingPosition] = useState(false);

    const [requestedMapPosition, setRequestedMapPosition] = useState(null);
    const [lastMapPosition, setLastMapPosition] = useState(null);

    const [exploringLocationMode, setExploringLocationMode] = useState(false);
    const [exploringLocationSession, setExploringLocationSession] = useState(null);
    const [exploringLocationFilters, setExploringLocationFilters] = useState(DEFAULT_EXPLORING_FILTERS);
    const [initialRouteSubPosition, setInitialRouteSubPosition] = useState(null);

    // parameters from URL
    const query = new URLSearchParams(window.location.search);
    const sessionID = query.get("session_id");
    const shortcutID = query.get("shortcut_id");

    const openVideo = useCallback(
        (closestPoint) => {
            setUpdatingPosition(true);
            const _closestPointID = closestPoint.point[0];
            dispatch(
                selectMapPoint(_closestPointID, closestPoint.coordinates, isVideo, false, true, () => {
                    setUpdatingPosition(false);
                }),
            );
        },
        [dispatch, isVideo],
    );

    useEffect(() => {
        if (!selectedMarker || _.isEmpty(selectedMarker) || !geometry.length) {
            return;
        }

        let { lat, lon, coords } = selectedMarker;
        let closestPoint = null;

        if (coords) {
            coords = [coords[1], coords[0]];
            closestPoint = nearestPointOnGeometry(coords[0], coords[1], geometry);
        } else {
            closestPoint = nearestPointOnGeometry(lat, lon, geometry);
        }
        if (closestPoint) {
            openVideo(closestPoint);
        } else {
            notification.error({
                message: "No nearby session found",
            });
        }
    }, [geometry, openVideo, selectedMarker]);

    const session = useMemo(() => {
        return _.get(sessions, [playlistData.routeID], {});
    }, [sessions, playlistData]);

    useEffect(() => {
        const newValue = _.get(routePosition, "subposition", null);
        if (!initialRouteSubPosition && !_.isEqual(initialRouteSubPosition, newValue)) {
            setInitialRouteSubPosition(newValue);
        }
    }, [initialRouteSubPosition, routePosition]);

    const sessionBounds = useMemo(() => {
        return _.get(session, "bounds", null);
    }, [session]);

    const streamInfo = useMemo(() => {
        return _.get(session, ["stream_info"], []);
    }, [session]);

    const sources = useMemo(() => {
        const _sources = getSourceList(playlistData, streamInfo, session);
        return _sources;
    }, [playlistData, streamInfo, session]);

    const currentDashboardName = useMemo(() => {
        let description = _.get(currentDashboard, ["description"], null);
        if (!description) return null;
        if (description) description = `${description.trim()} - `;
        return description;
    }, [currentDashboard]);

    // get all shortcuts on page load
    useEffect(() => {
        dispatch(getShortcuts());
    }, [dispatch]);

    useEffect(() => {
        let layouts = {};
        _.forEach(sources, (source, index) => {
            const videoIndex = _.get(source, ["streams", 0, "videoIndex"], null);
            layouts[index] = videoIndex;
        });
        setSourcesMap(layouts);
    }, [setSourcesMap, sources]);

    // when sessionBounds request map to go to session bounds
    useEffect(() => {
        if (sessionBounds) {
            dispatch(goToBounds(sessionBounds));
        }
    }, [dispatch, sessionBounds]);

    // request map position change with a bit of delay to allow map to finish resizing
    useEffect(() => {
        const timer = setTimeout(() => {
            if (requestedMapPosition) {
                dispatch(goToBounds(requestedMapPosition));
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [dispatch, requestedMapPosition]);

    // load OHL Structure data....
    useEffect(() => {
        if (initialBounds) {
            // go to initial bounds first
            dispatch(goToBounds(initialBounds));
            dispatch(
                getNearbyAssets(
                    1,
                    false,
                    (assets) => {
                        if (assets) {
                            if (assets.length > 0) {
                                const filteredAssets = _.filter(assets, (asset) => {
                                    // filter by allowed asset types
                                    if (!_.includes(ALLOWED_ASSETS_TYPES, asset.asset_type)) {
                                        return false;
                                    }

                                    // check for matching trid in asset data (This is session TRACK)
                                    try {
                                        const data = JSON.parse(asset.data || "{}");
                                        return _.get(data, "trid", null) === initialRouteSubPosition;
                                    } catch (error) {
                                        return false;
                                    }
                                });
                                dispatch(setAssetSearchType({ searchType: "nearby" }));
                                dispatch(receiveNearbyAssets(filteredAssets));
                            } else {
                                dispatch(receiveNearbyAssets([]));
                            }
                        }
                    },
                    false,
                    initialBounds,
                ),
            );
        }
    }, [initialBounds, initialRouteSubPosition, dispatch]);

    const selectedShortcut = React.useMemo(() => {
        return shortcuts.find((shortcut) => shortcut.id === parseInt(shortcutID));
    }, [shortcuts, shortcutID]);

    const shortcutsHasSession = useMemo(() => {
        if (Object.keys(shortcuts).length && selectedShortcut) {
            return true;
        }
        return false;
    }, [shortcuts, selectedShortcut]);

    const selectedShortcutSections = useMemo(() => {
        const sections = _.get(selectedShortcut, ["config", "sections"], []);
        return sections;
    }, [selectedShortcut]);

    const shortcutName = useMemo(() => {
        return _.get(selectedShortcut, ["config", "name"], null);
    }, [selectedShortcut]);

    const distanceUnits = useMemo(() => {
        if (elrUnits === "elr_mile_chain") {
            return "ELR Mile & Chain";
        } else if (elrUnits === "elr_mile_yards") {
            return "ELR Mile & Yards";
        } else if (elrUnits === "elr_meterage") {
            return "ELR & Meterage";
        }
    }, [elrUnits]);

    // pull history for a location when explore mode is turned on
    useEffect(() => {
        if (exploringLocationMode) {
            const currentLocation = parseRoutePositionData(routePosition, routeSystemID);
            dispatch(getPagedRouteHistory(currentLocation, true, false));
        }
    }, [exploringLocationMode, selectedSection, dispatch, routePosition, routeSystemID]);

    useEffect(() => {
        if (selectedShortcutSections.length > 1) {
            Modal.confirm({
                title: "Which section do you want to patrol?",
                className: "OlePatrolsSectionSelectModal",
                width: "60vw",
                content: (
                    <div>
                        {selectedShortcutSections.map((section, index) => {
                            const { elr, start_chain, end_chain } = section;
                            return (
                                <div
                                    key={index}
                                    style={{ marginBottom: "8px", cursor: "pointer", padding: "4px", border: "1px solid #555" }}
                                    onClick={() => {
                                        setSelectedSection(section);
                                        Modal.destroyAll();
                                    }}>
                                    <strong>Section {index + 1}:</strong> ELR: {elr}, Start Chain: {start_chain}, End Chain: {end_chain}
                                </div>
                            );
                        })}
                    </div>
                ),
                okText: "Load OLE Patrol",
                cancelButtonProps: { style: { display: "none" } },
                onOk() {
                    // do nothing, wait for user to select section which will close the modal
                },
            });
        } else {
            setSelectedSection(selectedShortcutSections[0]);
        }
    }, [selectedShortcutSections]);

    // override page title to add "- OLE Patrols" at the end
    useEffect(() => {
        const observer = new MutationObserver(() => {
            // document.title = "My Override Title";
            if (titleUpdatedRef.current) return;
            titleUpdatedRef.current = true;
            document.title = `${document.title} - OLE Patrols`;
        });

        observer.observe(document.querySelector("title"), {
            subtree: true,
            characterData: true,
            childList: true,
        });

        return () => observer.disconnect();
    }, []);

    const availableBearings = useMemo(() => {
        let bearings = getBearingOptions(consolidatedHistory);
        return bearings;
    }, [consolidatedHistory]);

    const currentSessionDirection = useMemo(() => {
        if (!consolidatedHistory || !sessionID) return null;
        const selectedSessionIndex = _.findIndex(consolidatedHistory, (item) => item.session_id === parseInt(sessionID));
        if (selectedSessionIndex === -1) return null;

        return calculateDirectionText(_.get(consolidatedHistory, [selectedSessionIndex, "session_tags"], []));
    }, [consolidatedHistory, sessionID]);

    const currentSessionPositionBearing = useMemo(() => {
        if (!consolidatedHistory || !sessionID) return null;
        const selectedSessionIndex = _.findIndex(consolidatedHistory, (item) => item.session_id === parseInt(sessionID));
        if (selectedSessionIndex === -1) return null;

        return calculateBearingText(_.get(consolidatedHistory, [selectedSessionIndex, "bearing"], null));
    }, [consolidatedHistory, sessionID]);

    const oppositeCurrentSessionDirection = useMemo(() => {
        return currentSessionDirection === "Forward" ? "Backward" : "Forward";
    }, [currentSessionDirection]);

    const oppositeCurrentSessionBearing = useMemo(() => {
        const newBearingIndex = _.findIndex(availableBearings, (b) => b !== currentSessionPositionBearing);
        return availableBearings[newBearingIndex];
    }, [availableBearings, currentSessionPositionBearing]);

    const filteredHistoryList = useMemo(() => {
        let newList = [];

        // filters to go here later....
        _.forEach(consolidatedHistory, (historyItem) => {
            let include = true;

            // exclude current session
            if (historyItem.session_id === parseInt(sessionID)) {
                include = false;
            }

            // Apply day_time filter
            if (include && exploringLocationFilters.day_time && exploringLocationFilters.day_time !== "Any") {
                if (!_.includes(historyItem.session_tags, exploringLocationFilters.day_time)) {
                    include = false;
                }
            }

            // apply video_facing filter
            if (include && exploringLocationFilters.video_facing && exploringLocationFilters.video_facing !== -1) {
                const itemBearingText = calculateBearingText(historyItem.bearing);
                const itemDirectionTest = calculateDirectionText(historyItem.session_tags);

                if (exploringLocationFilters.video_facing === "same") {
                    // facing same way as current session
                    if (
                        !(
                            currentSessionPositionBearing &&
                            oppositeCurrentSessionDirection &&
                            ((currentSessionPositionBearing === itemBearingText && currentSessionDirection === itemDirectionTest) ||
                                (oppositeCurrentSessionBearing === itemBearingText && oppositeCurrentSessionDirection === itemDirectionTest))
                        )
                    ) {
                        include = false;
                    }
                } else if (exploringLocationFilters.video_facing === "opposite") {
                    // facing opposite way to current session
                    if (
                        currentSessionPositionBearing &&
                        oppositeCurrentSessionDirection &&
                        ((currentSessionPositionBearing === itemBearingText && currentSessionDirection === itemDirectionTest) ||
                            (oppositeCurrentSessionBearing === itemBearingText && oppositeCurrentSessionDirection === itemDirectionTest))
                    ) {
                        include = false;
                    }
                }
            }

            // Apply extra_type filter
            if (include && exploringLocationFilters.extra_type && exploringLocationFilters.extra_type !== -1) {
                if (!_.includes(historyItem.session_tags, exploringLocationFilters.extra_type)) {
                    include = false;
                }
            }

            // days in past filter
            if (include && exploringLocationFilters.days_in_past && exploringLocationFilters.days_in_past > 0) {
                const daysInPastDate = new Date();
                daysInPastDate.setDate(daysInPastDate.getDate() - exploringLocationFilters.days_in_past);
                if (new Date(historyItem.timestamp) < daysInPastDate) {
                    include = false;
                }
            }

            if (include) {
                newList.push(historyItem);
            }
        });

        newList = _.uniqBy(newList, (item) => item.session_id).reverse();

        return newList;
    }, [
        consolidatedHistory,
        currentSessionDirection,
        currentSessionPositionBearing,
        exploringLocationFilters,
        oppositeCurrentSessionBearing,
        oppositeCurrentSessionDirection,
        sessionID,
    ]);

    const positionFilter = useMemo(() => {
        if (!selectedSection) return null;
        const { elr, start_chain: start_pos, end_chain: end_pos } = selectedSection;
        return {
            elr: _.toUpper(elr),
            start_pos,
            end_pos,
        };
    }, [selectedSection]);

    useEffect(() => {
        if (selectedSection) {
            dispatch(selectShortcutSession(shortcutID, sessionID, false, positionFilter, true));
        }
    }, [sessionID, shortcutID, selectedSection, dispatch, positionFilter]);

    const toggleStillsMode = useCallback(
        (stills = false) => {
            if (isStillsMode === stills) {
                return;
            }

            setPlayButtonPaused(true);

            let newIndex = 0;
            const currentPlaylist = playlistData.video[currentSourceIndex];

            let currentPlaylistItem = _.get(currentPlaylist, currentIndex, null);

            // Fallback to first item if currentIndex is out of bounds
            if (!currentPlaylistItem) {
                currentPlaylistItem = currentPlaylist[0];
            }

            // Get geometry and offsets
            const geometry = playlistData.route_locations_v2;
            const currentSourceOffset = sourceOffsetSelector({ playlist: fullPlaylist, sessions: sessions });
            const targetSourceOffset = sourceOffsetSelector({ playlist: fullPlaylist, sessions: sessions }, currentSourceIndex);
            let newTimestamp = videoTimestampExtractor(currentPlaylistItem); // Fallback in case playlistDistance is not available

            const offsetDiff = currentSourceOffset - targetSourceOffset;
            // Try to use playlistDistanceToTimestamp to get the timestamp for the target camera
            if (geometry && playlistDistance) {
                const adjustedDistance = playlistDistance + offsetDiff;
                newTimestamp = playlistDistanceToTimestamp(adjustedDistance, geometry);
            }

            // Find the closest index in the new playlist for the calculated timestamp
            const newPlaylist = playlistData.video[currentSourceIndex];
            newIndex = absoluteTimeLookup(newTimestamp, newPlaylist);

            dispatch(requestPlaylistPosition(isVideo, isEnhanced, !isStillsMode, currentSourceIndex, newIndex, currentTimeOffset));
        },
        [
            dispatch,
            isStillsMode,
            currentIndex,
            currentSourceIndex,
            playlistData,
            fullPlaylist,
            sessions,
            isVideo,
            isEnhanced,
            currentTimeOffset,
            playlistDistance,
        ],
    );

    const isPlayingVideo = useMemo(() => {
        return !isStillsMode && playerState === "playing";
    }, [isStillsMode, playerState]);

    const handleTogglePlayerState = useCallback(() => {
        dispatch(videoOrImagePlayerTogglePlay());
        setPlayButtonPaused((prevState) => !prevState);
    }, [dispatch]);

    const handleSeek = useCallback(
        (seekByValue) => {
            dispatch(videoPlayerSeekTo(seekByValue));
        },
        [dispatch],
    );

    const calculatedELR = useMemo(() => {
        if (routePosition) {
            const calculatedELR = parseRoutePositionData(routePosition, routeSystemID);
            if (calculatedELR) {
                return {
                    string: calculatedELR.to_string(elrUnits, datumOffsets),
                    route: calculatedELR.route,
                    track: calculatedELR.track,
                    position: calculatedELR.position,
                };
            }
        }
    }, [routePosition, routeSystemID, elrUnits, datumOffsets]);

    const positionElrString = useMemo(() => {
        if (calculatedELR) {
            return calculatedELR.string;
        }
        return "N/A";
    }, [calculatedELR]);

    const haveAdditionalSources = useMemo(() => {
        return sources.length > 1;
    }, [sources]);

    const renderedSources = useMemo(() => {
        let renderedSources = (
            <div className="OlePatrolsPageSidePanelLoading">
                <OBCSpinner
                    color={"#e8dfff"}
                    size={13}
                />
                <div>Loading...</div>
            </div>
        );

        if (sources.length === 1) {
            renderedSources = (
                <div className="OlePatrolsPageSidePanelNoSources">
                    <div>
                        <FontAwesomeIcon
                            icon={faVideoSlash}
                            size="3x"
                        />
                    </div>
                    <div>No additional cameras available</div>
                </div>
            );
        }

        if (sources.length > 1) {
            let newList = [];
            _.forEach(sourcesMap, (sourceIndex, position) => {
                if (parseInt(position) !== 0) {
                    const source = _.find(sources, (src) => _.get(src.streams, [0, "videoIndex"]) === parseInt(sourceIndex));
                    newList.push(source);
                }
            });

            renderedSources = _.map(newList, (source, index) => {
                const videoIndex = _.get(source.streams, [0, "videoIndex"]);
                return (
                    <div
                        key={index}
                        className="OlePatrolsPageSidePanelSourceItem">
                        <OlePatrolsStillsPreview
                            sourceIndex={videoIndex}
                            imageTitle={source.name}
                            updatePossible={!isPlayingVideo}
                            loadSourceIndex={(sourceIndex) => {
                                const currentMainSourceIndex = currentSourceIndex;
                                const _sourceIndex = _.findKey(sourcesMap, (key) => key === sourceIndex);
                                setSourcesMap((prevMap) => {
                                    const newMap = { ...prevMap };
                                    newMap[_.parseInt(_sourceIndex)] = currentMainSourceIndex;
                                    newMap[0] = parseInt(sourceIndex);
                                    return newMap;
                                });

                                const currentPlaylist = _.get(playlistData, ["video", sourceIndex], []);
                                const { index, videoOffset } = findIndexAtDistanceV2(playlistDistance, currentPlaylist, routeLocationsV2);

                                dispatch(requestPlaylistPosition(isVideo, isEnhanced, isStillsMode, sourceIndex, index, videoOffset));
                            }}
                        />
                    </div>
                );
            });
        }

        return renderedSources;
    }, [
        sources,
        sourcesMap,
        isPlayingVideo,
        currentSourceIndex,
        playlistData,
        playlistDistance,
        routeLocationsV2,
        dispatch,
        isVideo,
        isEnhanced,
        isStillsMode,
    ]);

    const getPositionForImage = useCallback(
        (railImage, removeLabel) => {
            if (!routeLocationsV2) {
                return null;
            }
            const system = _.find(RouteCoordinateSystems, (system) => {
                return system.ID === routeSystemID;
            });

            const positionExtractor = (railImage) => {
                const timestamp = videoKeyToTimestamp(railImage[0]);
                const routePosition = getRoutePosAtTimestamp(timestamp / 1000, routeLocationsV2, routeSystemID);
                if (!routePosition) {
                    return null;
                }
                return {
                    elr: routePosition.route,
                    position: routePosition.position,
                };
            };

            const posData = positionExtractor(railImage);
            if (!posData) {
                return null;
            }
            const calculatedELR = system.create(posData.elr, posData.position);
            if (calculatedELR) {
                if (calculatedELR && calculatedELR.to_simple_string) {
                    const elr = calculatedELR.to_simple_string(elrUnits, [], removeLabel);
                    return elr;
                } else if (calculatedELR && calculatedELR.to_string) {
                    const elr = calculatedELR.to_string(elrUnits, [], removeLabel);
                    return elr;
                }
            }
            return null;
        },
        [elrUnits, routeLocationsV2, routeSystemID],
    );

    const toggleExploreLocationMode = useCallback(
        (visible) => {
            // if turning on exploring mode, save last map position and request map to go to current position
            if (visible) {
                setLastMapPosition(currentMapBounds);
                const ADJUSTMENT_VALUE = 0.00001;

                let north = currentPositionCoords[1] + ADJUSTMENT_VALUE;
                let east = currentPositionCoords[0] + ADJUSTMENT_VALUE;
                let south = currentPositionCoords[1] - ADJUSTMENT_VALUE;
                let west = currentPositionCoords[0] - ADJUSTMENT_VALUE;
                setRequestedMapPosition({ north, east, south, west });
            } else {
                // if turning off exploring mode, restore last map position
                if (lastMapPosition) {
                    setRequestedMapPosition(lastMapPosition);
                }
                setExploringLocationSession(null);
                setExploringLocationFilters(DEFAULT_EXPLORING_FILTERS);
            }
            setExploringLocationMode(visible);
        },
        [currentMapBounds, currentPositionCoords, lastMapPosition],
    );

    const currentPositionComponent = useMemo(() => {
        return (
            <div className="OlePatrolsPageBottomSection">
                <div className="OlePatrolsPageBottomTitle">
                    <strong>Position</strong>
                    <FontAwesomeIcon
                        className="OlePatrolsPageBottomUnitSettingsIcon"
                        onClick={(e) => {
                            e.stopPropagation();
                            setUnitSelectionModalVisible(true);
                        }}
                        icon={faCog}
                    />
                </div>
                <div className="OlePatrolsPageBottomItem">
                    <strong>Current Location:</strong>
                    {distanceUnits}
                    <div className="VerticalDivider"></div>
                    {positionElrString}
                    {!exploringLocationMode && (
                        <>
                            <div className="VerticalDivider"></div>
                            <Button
                                className="OlePatrolsExploreButton"
                                size="small"
                                disabled={!playButtonPaused}
                                onClick={() => {
                                    toggleExploreLocationMode(true);
                                    if (toolMode !== "zoom") dispatch(changeToolMode("zoom"));
                                }}
                                type="primary">
                                <FontAwesomeIcon icon={faSearchLocation} /> Explore Location
                            </Button>
                        </>
                    )}
                </div>

                <div className="OlePatrolsPageBottomTitle">
                    <strong>Patrol Details:</strong>
                </div>
                <div className="OlePatrolsPageBottomItem">
                    <strong>Session ID:</strong>
                    <span>#{session.id}</span>
                </div>

                <div className="OlePatrolsPageBottomItem">
                    <strong>Route Name:</strong>
                    <span>{session.route_name}</span>
                </div>

                <div className="OlePatrolsPageBottomItem">
                    <strong>Session date:</strong>
                    <span>{moment.unix(session.first_seen).format("DD MMM YYYY")}</span>
                </div>
            </div>
        );
    }, [distanceUnits, positionElrString, exploringLocationMode, playButtonPaused, toggleExploreLocationMode, toolMode, dispatch, session]);

    // TODO: make use of camera offset for more precise positioning - this will probably have to be used at some point so leaving it here....
    // const cameraOffset = useMemo(() => {
    //     const sessionTags = _.get(session, "session_tags", []);

    //     const sessionIsBackward = _.includes(sessionTags, "Backward");
    //     const currentOffset = _.get(cameraOffsets, currentSourceIndex, 0);
    //     if (sessionIsBackward) {
    //         return -currentOffset;
    //     }
    //     return currentOffset;
    // }, [cameraOffsets, currentSourceIndex, session]);

    const mapComponent = useMemo(() => {
        return (
            <div className={`OlePatrolsPageSidePanelMap`}>
                {updatingPosition ? (
                    <div className="OlePatrolsPageSidePanelMapLoadingOverlay">
                        <OBCSpinner
                            color={"#e8dfff"}
                            size={60}
                        />
                    </div>
                ) : null}
                <MapComponent
                    extraStyle={{ height: "100%" }}
                    resizingMap={resizingMap}
                    stripped={"ole_patrols"}
                    customMarkerOnClickAction={(asset) => {
                        if (exploringLocationMode) {
                            return;
                        }
                        const coordinates = [asset.lon, asset.lat];
                        const currentPlaylist = playlistData.video[currentSourceIndex];
                        const playlistDistance = coordinateToPlaylistDistance(coordinates, routeLocationsV2.distance_to_coordinate);

                        const { index, videoOffset } = findIndexAtDistanceV2(playlistDistance, currentPlaylist, routeLocationsV2);

                        dispatch(requestPlaylistPosition(true, false, isStillsMode, currentSourceIndex, index, videoOffset));
                    }}
                    interactive={!exploringLocationMode}
                />
            </div>
        );
    }, [currentSourceIndex, dispatch, exploringLocationMode, isStillsMode, playlistData.video, resizingMap, routeLocationsV2, updatingPosition]);

    const elrLabelsNew = useMemo(() => {
        const currentPlaylist = _.get(playlistData, ["video", currentSourceIndex], []);
        const maxSlider = currentPlaylist.length - 1;

        const elrIndexes = calculateElrGroupsForVideo(currentPlaylist, routeLocationsV2, routeSystemID, maxSlider, 0);

        let marks = {};

        if (currentPlaylist && currentPlaylist.length) {
            // top labels - elrs
            for (let i = 0; i < elrIndexes.length; i++) {
                const elr = elrIndexes[i].routeID;
                const index = elrIndexes[i].start;
                if (elr) {
                    marks[index] = [
                        {
                            label: (
                                <div className="custom-slider-ole-patrols-elr-label">
                                    <strong>{elr}</strong>
                                </div>
                            ),
                            position: "top_upper",
                            tooltip: (
                                <div className="custom-slider-ole-patrols-tooltip-item">
                                    <strong>{elr}</strong>
                                </div>
                            ),
                            type: "elr_marker",
                        },
                    ];
                }
            }

            // bottom labels - elrs
            const interval = Math.floor(currentPlaylist.length / 5);

            for (let i = 0; i <= 5; i++) {
                let index = interval * i;
                let scrollerIndex = interval * i;
                if (i === 5) {
                    index -= 1;
                }
                if (scrollerIndex === currentPlaylist.length) {
                    scrollerIndex -= 1;
                }

                if (currentPlaylist[index]) {
                    let removeLabel = true;

                    if (i === 0 || i === 5) {
                        removeLabel = false;
                    }

                    const elr = getPositionForImage(currentPlaylist[index], removeLabel);
                    if (elr) {
                        marks[scrollerIndex] = [
                            {
                                label: <strong>{elr}</strong>,
                                position: "bottom",
                                type: "elr_marker",
                            },
                        ];
                    }
                }
            }
        }

        // asset markers (OHL Structures etc) top
        if (routeLocationsV2 && assetsFound && assetsFound.length) {
            _.forEach(assetsFound, (asset) => {
                const coordinates = [asset.lon, asset.lat];
                const playlistDistance = coordinateToPlaylistDistance(coordinates, routeLocationsV2.distance_to_coordinate);
                const { index, videoOffset } = findIndexAtDistanceV2(playlistDistance, currentPlaylist, routeLocationsV2);

                const currentMarker = marks[index + videoOffset] || [];

                let icon = <FontAwesomeIcon icon={faMapMarkerAlt} />;

                if (asset.icon_name === "ohl") {
                    icon = <OhlSvg />;
                }

                marks[index + videoOffset] = [
                    ...currentMarker,
                    {
                        style: { fontSize: 10, whiteSpace: "nowrap", padding: "3px 4px" },
                        label: <div className="custom-slider-ole-patrols-icon">{icon}</div>,
                        tooltip: (
                            <div className="custom-slider-ole-patrols-tooltip-item">
                                <div>{asset.asset_type}</div>
                                <strong>{asset.asset_id}</strong>
                            </div>
                        ),
                        position: "top_lower",
                        type: "asset_marker",
                    },
                ];
            });
        }

        return marks;
    }, [playlistData, currentSourceIndex, routeLocationsV2, routeSystemID, assetsFound, getPositionForImage]);

    const exactCurrentIndex = useMemo(() => {
        if (!routeLocationsV2 || !currentPositionCoords) {
            return null;
        }
        const currentPlaylist = _.get(playlistData, ["video", currentSourceIndex], []);
        const { index, videoOffset } = findIndexAtDistanceV2(playlistDistance, currentPlaylist, routeLocationsV2);

        return index + videoOffset;
    }, [currentPositionCoords, currentSourceIndex, playlistData, playlistDistance, routeLocationsV2]);

    const slider = useMemo(() => {
        const playlistDataVideo = _.get(playlistData, "video", []);
        const videoList = _.get(playlistDataVideo, [currentSourceIndex], []);
        const maxSlider = videoList.length - 1;

        return (
            <div>
                <CustomSlider
                    className="custom-slider-ole-patrols"
                    min={0}
                    max={maxSlider}
                    value={exactCurrentIndex}
                    onChange={(newIndex) => {
                        const subIndex = _.floor(newIndex % 1, 10);
                        dispatch(requestPlaylistPosition(true, false, isStillsMode, currentSourceIndex, parseInt(newIndex), subIndex));
                    }}
                    marks={elrLabelsNew}
                    step={0.01}
                    included={false}
                    tipFormatter={null}
                />
            </div>
        );
    }, [playlistData, currentSourceIndex, elrLabelsNew, isStillsMode, dispatch, exactCurrentIndex]);

    const mainPlayerActiveClass = useMemo(() => {
        if (!mainPlayerLoaded) return "Loading";
        if (!exploringLocationMode) return "Active";
        return "";
    }, [exploringLocationMode, mainPlayerLoaded]);

    const playerWithSliderComponent = useMemo(() => {
        let zoomTitle = toolMode ? "Exit Zoom Mode" : "Enable Zoom";
        if (!playButtonPaused) {
            zoomTitle = "Pause Player to Enable Zoom";
        }
        return (
            <div className="OlePatrolsPagePlayerMain">
                <div className="OlePatrolsVideoPlayerWrapper">
                    <div className={`OlePatrolsVideoPlayer ${mainPlayerActiveClass}`}>
                        <VideoOrImageComponent
                            viewMode="ole-patrols"
                            playerControls={!exploringLocationMode}
                        />
                    </div>
                    {exploringLocationMode && <div className="OlePatrolsExploringModeInfoPanel">{currentPositionComponent}</div>}
                    {exploringLocationMode && mapComponent}
                </div>
                {!exploringLocationMode && (
                    <div className="OlePatrolsVideoTimeline">
                        {/* LEFT CONTROL BUTTONS */}
                        <div className="OlePatrolsVideoTimelineLeft">
                            {/* Play button */}
                            <Button
                                onClick={() => handleTogglePlayerState()}
                                type={playButtonPaused ? "default" : "primary"}
                                icon={playButtonPaused ? "caret-right" : "pause"}
                                disabled={playerOrImageLoading}
                                size={CONTROL_BUTTONS_SIZE}
                            />

                            {/* backward skip 10 seconds */}
                            <Button
                                onClick={() => handleSeek(-10)}
                                icon="step-backward"
                                size={CONTROL_BUTTONS_SIZE}
                                disabled={playerOrImageLoading}
                            />

                            {/* forward skip 10 seconds */}
                            <Button
                                onClick={() => handleSeek(10)}
                                icon="step-forward"
                                size={CONTROL_BUTTONS_SIZE}
                                disabled={playerOrImageLoading}
                            />
                        </div>

                        {/* PLAYER SLIDER */}
                        <div className="OlePatrolsVideoTimelineCenter">{slider}</div>

                        {/* RIGHT CONTROL BUTTONS */}
                        <div className="OlePatrolsVideoTimelineRight">
                            {/* PIP Button - DISABLED FOR NOW MIGHT BE ENABLED LATER IF WE DECIDE TO HAVE IT... */}
                            {/* <Tooltip title={isStillsMode ? "Toggle Picture-in-Picture" : "Picture-in-Picture unavailable in video Mode"}>
                                <Button
                                    className="ToolBarButtonWithIcon"
                                    type={showPictureInPicture ? "primary" : "default"}
                                    disabled={!isStillsMode}
                                    onClick={() => {
                                        dispatch(togglePictureInPicture(!showPictureInPicture));
                                    }}
                                    size={CONTROL_BUTTONS_SIZE}>
                                    <FontAwesomeIcon icon={showPictureInPicture ? windowRestoreBold : faWindowRestore} />
                                </Button>
                            </Tooltip> */}

                            <Popover content={<ImageAdjustmentsComponent />}>
                                <Button
                                    className="ToolBarButtonWithIcon"
                                    disabled={!isStillsMode}>
                                    <FontAwesomeIcon icon={faSlidersH} />
                                </Button>
                            </Popover>

                            <Tooltip title={playButtonPaused ? "Take Snapshot" : "Pause Player to Take Snapshot"}>
                                <Button
                                    className="ToolBarButtonWithIcon"
                                    disabled={!mainPlayerLoaded || !playButtonPaused}
                                    onClick={() => dispatch(toggleSnapshot())}>
                                    <FontAwesomeIcon icon={faImage} />
                                </Button>
                            </Tooltip>

                            {/* Zoom button */}
                            <Tooltip title={zoomTitle}>
                                <Button
                                    className="ToolBarButtonWithIcon"
                                    type={toolMode ? "primary" : "default"}
                                    disabled={!isStillsMode || !playButtonPaused}
                                    onClick={() => {
                                        dispatch(changeToolMode(toolMode === "zoom" ? null : "zoom"));
                                    }}
                                    size={CONTROL_BUTTONS_SIZE}>
                                    <FontAwesomeIcon icon={faSearchPlus} />
                                </Button>
                            </Tooltip>

                            {/* Image/Video player toggle buttons */}
                            <ButtonGroup style={{ paddingLeft: "7px" }}>
                                <Button
                                    type={isStillsMode ? "primary" : "default"}
                                    icon="camera"
                                    size={CONTROL_BUTTONS_SIZE}
                                    onClick={() => toggleStillsMode(true)}
                                />
                                <Button
                                    type={!isStillsMode ? "primary" : "default"}
                                    icon="video-camera"
                                    size={CONTROL_BUTTONS_SIZE}
                                    onClick={() => {
                                        toggleStillsMode(false);
                                        if (toolMode) dispatch(changeToolMode(null));
                                        if (showPictureInPicture) dispatch(togglePictureInPicture(!showPictureInPicture));
                                    }}
                                />
                            </ButtonGroup>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [
        mainPlayerActiveClass,
        exploringLocationMode,
        currentPositionComponent,
        mapComponent,
        playButtonPaused,
        playerOrImageLoading,
        slider,
        mainPlayerLoaded,
        toolMode,
        isStillsMode,
        handleTogglePlayerState,
        handleSeek,
        dispatch,
        toggleStillsMode,
        showPictureInPicture,
    ]);

    const triggerResizeMap = useCallback(() => {
        if (resizingMap) {
            return;
        }
        setResizingMap(true);
        setTimeout(() => {
            setResizingMap(false);
        }, 0);
    }, [resizingMap]);

    const additionalSourcesAndMapComponent = useMemo(() => {
        return (
            <div className="OlePatrolsPageSidePanelMain">
                <ResizableDivider
                    initialSize={haveAdditionalSources ? "70%" : "30%"} // left panel initial size
                    minSize={40} // minimum size for left or right panel
                    orientation="vertical"
                    onDragStart={() => setResizingMap(true)}
                    onDragEnd={() => setResizingMap(false)}
                    onSizeChanged={() => {
                        triggerResizeMap();
                    }}
                    left={
                        <div className="OlePatrolsPageSidePanelTop">
                            <strong>Additional Camera Sources</strong>
                            <div className="OlePatrolsAdditionalSources">{renderedSources}</div>
                        </div>
                    }
                    right={mapComponent}
                />
            </div>
        );
    }, [renderedSources, haveAdditionalSources, mapComponent, triggerResizeMap]);

    const exploringComponent = useMemo(() => {
        return (
            <div className="OlePatrolsExploringMain">
                <div className="OlePatrolsExploringTopBar">
                    <Button
                        className="OlePatrolsExploreButton"
                        onClick={() => {
                            toggleExploreLocationMode(false);
                            if (toolMode === "zoom") dispatch(changeToolMode(null));
                        }}
                        type="primary"
                        icon="left">
                        Go Back To Patrol
                    </Button>
                    <div className="OlePatrolsExploringTitle">Explore Location using different recordings</div>
                </div>
                <div className="OlePatrolsExploringContent">
                    <div className="OlePatrolsExploringPlayer">
                        <StandAloneVideoOrImageComponent
                            selectedSession={exploringLocationSession}
                            positionFilter={selectedSection}
                            startPosition={routePosition}
                        />
                    </div>

                    <div className="OlePatrolsExploringSessionsComponent">
                        <div className="OlePatrolsExploringSessionsFilters">
                            <div className="Left">
                                {/* Time of Day */}
                                <div className="OlePatrolsExploringSessionsFiltersRow">
                                    <div className="OlePatrolsExploringSessionsFiltersTitle">Time of Day</div>
                                    <div className="OlePatrolsExploringSessionsFiltersInput">
                                        <Select
                                            placeholder="Filter day time"
                                            value={_.get(exploringLocationFilters, "day_time", "Day")}
                                            onChange={(value) => {
                                                setExploringLocationFilters((prevState) => {
                                                    return {
                                                        ...prevState,
                                                        day_time: value,
                                                    };
                                                });
                                            }}
                                            style={{ width: "75px" }}
                                            size="small">
                                            <Select.Option value="Any">Any</Select.Option>
                                            <Select.Option value="Day">Day</Select.Option>
                                            <Select.Option value="Night">Night</Select.Option>
                                        </Select>
                                    </div>
                                </div>

                                {/* Video Filter */}
                                <div className="OlePatrolsExploringSessionsFiltersRow">
                                    <div className="OlePatrolsExploringSessionsFiltersTitle">Video</div>
                                    <div className="OlePatrolsExploringSessionsFiltersInput">
                                        <Select
                                            placeholder="Pantograph Only"
                                            value={_.get(exploringLocationFilters, "extra_type", false)}
                                            onChange={(value) => {
                                                setExploringLocationFilters((prevState) => {
                                                    return {
                                                        ...prevState,
                                                        extra_type: value,
                                                    };
                                                });
                                            }}
                                            dropdownMatchSelectWidth={false}
                                            size="small">
                                            <Select.Option value={-1}>Any</Select.Option>
                                            <Select.Option value={"Visible Pantograph"}>Visible Pantograph</Select.Option>
                                            <Select.Option value={"Visible FFV"}>Visible FFV</Select.Option>
                                        </Select>
                                    </div>
                                </div>
                                {/* Session date range */}
                                <div className="OlePatrolsExploringSessionsFiltersRow">
                                    <div className="OlePatrolsExploringSessionsFiltersTitle">Date Range</div>
                                    <div className="OlePatrolsExploringSessionsFiltersInput">
                                        <Select
                                            placeholder="Pantograph Only"
                                            value={_.get(exploringLocationFilters, "days_in_past", -1)}
                                            onChange={(value) => {
                                                setExploringLocationFilters((prevState) => {
                                                    return {
                                                        ...prevState,
                                                        days_in_past: value,
                                                    };
                                                });
                                            }}
                                            dropdownMatchSelectWidth={false}
                                            size="small">
                                            <Select.Option value={-1}>All</Select.Option>
                                            <Select.Option value={7}>Last 7 Days</Select.Option>
                                            <Select.Option value={14}>Last 14 Days</Select.Option>
                                        </Select>
                                    </div>
                                </div>

                                {/* Video Facing */}
                                <div className="OlePatrolsExploringSessionsFiltersRow">
                                    <div className="OlePatrolsExploringSessionsFiltersTitle">Video Facing</div>
                                    <div className="OlePatrolsExploringSessionsFiltersInput">
                                        <Select
                                            showSearch
                                            placeholder="Video Facing"
                                            value={_.get(exploringLocationFilters, "video_facing", -1)}
                                            onChange={(value) => {
                                                setExploringLocationFilters((prevState) => {
                                                    return {
                                                        ...prevState,
                                                        video_facing: value,
                                                    };
                                                });
                                            }}
                                            dropdownMatchSelectWidth={false}
                                            size="small">
                                            <Select.Option value={-1}>Any</Select.Option>
                                            <Select.Option value={"same"}>The same as patrol</Select.Option>
                                            <Select.Option value={"opposite"}>Opposite to patrol</Select.Option>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Right side */}
                            <div className="Right">
                                <div className="OlePatrolsExploringPlayerControls">
                                    <div></div>
                                </div>
                            </div>
                        </div>

                        <div className="OlePatrolsExploringSessionsListMain">
                            {fetchingHistory ? (
                                <div className="LoadingSpinnerCenter">
                                    <div>
                                        <OBCSpinner
                                            color={"#e8dfff"}
                                            size={60}
                                        />
                                    </div>
                                    <p>Loading nearby sessions...</p>
                                </div>
                            ) : (
                                <div className="OlePatrolsExploringSessionsList">
                                    {filteredHistoryList.length ? (
                                        _.map(filteredHistoryList, (session) => {
                                            return (
                                                <SessionListItem
                                                    key={session.session_id}
                                                    active={exploringLocationSession && exploringLocationSession.id === session.session_id}
                                                    session={session}
                                                    setExploringLocationSession={setExploringLocationSession}
                                                />
                                            );
                                        })
                                    ) : (
                                        <div className="NoSessionsFound">No sessions found matching the selected filters.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [
        dispatch,
        exploringLocationFilters,
        exploringLocationSession,
        fetchingHistory,
        filteredHistoryList,
        routePosition,
        selectedSection,
        toggleExploreLocationMode,
        toolMode,
    ]);

    if (!sessionID) {
        return (
            <div className="OlePatrolsPage">
                <div className="OlePatrolsPageErrorContainer">
                    <h2>No Session ID Provided</h2>
                    <p>Please provide a valid session ID or select an existing session from shortcuts tab.</p>
                    <Button
                        icon="left"
                        onClick={() => {
                            const olePatrolUrl = new URL(window.location.origin);
                            window.location.href = olePatrolUrl.toString();
                        }}>
                        Go Back To AIVR
                    </Button>
                </div>
            </div>
        );
    }

    if (!currentDashboardName) {
        return (
            <div className="OlePatrolsPage">
                <div className="OlePatrolsPageErrorContainer">
                    <div>
                        <OBCSpinner
                            color={"#e8dfff"}
                            size={60}
                        />
                    </div>
                    <h3>Loading Patrol Data...</h3>
                </div>
            </div>
        );
    }

    if (!shortcutsHasSession) {
        return (
            <div className="OlePatrolsPage">
                <div className="OlePatrolsPageErrorContainer">
                    <h2>No Access to the Shortcut</h2>
                    <h3>
                        <strong>
                            <u>{_.get(currentDashboard, ["description"], "unknown")}</u>
                        </strong>{" "}
                        dashboard doesn't have access to the selected shortcut.
                    </h3>
                    <Button
                        icon="swap"
                        style={{ marginTop: "15px" }}
                        onClick={() => {
                            const olePatrolUrl = new URL(window.location);
                            const pathName = olePatrolUrl.pathname;
                            const params = olePatrolUrl.searchParams;

                            const urlRedirect = pathName + "?" + params.toString();
                            history.push("/login?redirect_url=" + encodeURIComponent(urlRedirect));
                            dispatch(changeDashboard());
                        }}>
                        Switch Workspace
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="OlePatrolsPage">
            {unitSelectionModalVisible && <UnitSelectionModal onClose={() => setUnitSelectionModalVisible(false)} />}

            <div className="OlePatrolsPageTop">
                <img
                    id="OlePatrolsPageLogo"
                    src="ole_patrols_logo.png"
                    alt="OLE PATROLS"
                />
                {currentDashboardName ? (
                    <>
                        <div>
                            <strong>{currentDashboardName}</strong>
                        </div>
                        <div>
                            <span>{shortcutName}</span>
                        </div>
                    </>
                ) : (
                    <OBCSpinner
                        size={25}
                        color={"#e8dfff"}
                    />
                )}
            </div>
            <div className={`OlePatrolsPageContent ${exploringLocationMode ? "ExploringLocationMode" : ""}`}>
                <ResizableDivider
                    initialSize={exploringLocationMode ? "30%" : "70%"} // left panel initial size
                    minSize={350} // minimum size for left or right panel
                    minSizeLeft={exploringLocationMode ? "30%" : "950px"} // minimum size for left panel
                    orientation="horizontal"
                    onDragStart={() => setResizingMap(true)}
                    onDragEnd={() => setResizingMap(false)}
                    onSizeChanged={triggerResizeMap}
                    left={playerWithSliderComponent}
                    disabled={exploringLocationMode}
                    right={exploringLocationMode ? exploringComponent : additionalSourcesAndMapComponent}
                />
            </div>

            {!exploringLocationMode && <div className="OlePatrolsPageBottom">{currentPositionComponent}</div>}
            <img
                id="OlePatrolsLogoOverlay"
                src="ole_patrols_logo.png"
                alt="OLE PATROLS"
            />
        </div>
    );
};

export default OlePatrolPage;
